<template>
  <AppLayout 
    page-title="Scheduling" 
    page-subtitle="Manage your work schedule and assignments"
  >
    <!-- Page Header -->
    <div class="mb-8 pt-8">
      <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
        <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
        Work Schedule
      </div>
      <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">Scheduling</h1>
      <p class="text-xl text-slate-300 mb-8 max-w-2xl">Manage your work schedule, view upcoming assignments, and coordinate with teams</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
        <p class="text-slate-400">Loading schedule data...</p>
      </div>
    </div>

    <!-- Quick Stats -->
    <div v-else class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="schedule-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">This Week</p>
          <p class="text-3xl font-bold text-white">{{ scheduleStats.thisWeek }}</p>
          <p class="text-sm text-emerald-400">shifts scheduled</p>
        </div>
      </div>

      <div class="schedule-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Hours Scheduled</p>
          <p class="text-3xl font-bold text-white">{{ scheduleStats.hoursScheduled }}h</p>
          <p class="text-sm text-cyan-400">this week</p>
        </div>
      </div>

      <div class="schedule-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Pending Requests</p>
          <p class="text-3xl font-bold text-white">{{ scheduleStats.pendingRequests }}</p>
          <p class="text-sm text-amber-400">awaiting approval</p>
        </div>
      </div>

      <div class="schedule-stat-card">
        <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
          <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-slate-400 mb-1">Active Jobs</p>
          <p class="text-3xl font-bold text-white">{{ scheduleStats.activeJobs }}</p>
          <p class="text-sm text-purple-400">currently assigned</p>
        </div>
      </div>
    </div>

    <!-- Calendar View -->
    <div v-if="!isLoading" class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-white">Weekly Schedule</h2>
        <div class="flex space-x-3">
          <button 
            @click="previousWeek"
            class="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white font-medium rounded-lg transition-colors"
          >
            Previous
          </button>
          <button 
            @click="nextWeek"
            class="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white font-medium rounded-lg transition-colors"
          >
            Next
          </button>
        </div>
      </div>

      <div class="grid grid-cols-7 gap-4">
        <div v-for="day in weekDays" :key="day.date" class="min-h-[200px]">
          <div class="text-center mb-3">
            <p class="text-sm text-slate-400">{{ day.name }}</p>
            <p class="text-lg font-bold text-white">{{ day.date }}</p>
          </div>
          <div class="space-y-2">
            <div 
              v-for="shift in day.shifts" 
              :key="shift.id"
              class="p-3 bg-slate-700/30 rounded-lg border-l-4"
              :class="getShiftBorderClass(shift.status)"
            >
              <p class="text-sm font-medium text-white">{{ shift.jobTitle }}</p>
              <p class="text-xs text-slate-400">{{ shift.time }}</p>
              <p class="text-xs text-slate-400">{{ shift.location }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upcoming Shifts -->
    <div v-if="!isLoading" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Upcoming Shifts</h3>
        <div class="space-y-4">
          <div v-for="shift in upcomingShifts" :key="shift.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
            <div>
              <p class="text-white font-medium">{{ shift.jobTitle }}</p>
              <p class="text-sm text-slate-400">{{ shift.date }} at {{ shift.time }}</p>
              <p class="text-sm text-slate-400">{{ shift.location }}</p>
            </div>
            <div class="text-right">
              <span :class="getStatusClass(shift.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                {{ shift.status }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 class="text-xl font-bold text-white mb-4">Quick Actions</h3>
        <div class="space-y-4">
          <button @click="showTimeOffModal = true" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Request Time Off</p>
                <p class="text-sm text-slate-400">Submit time off request</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button @click="showSwapShiftModal = true" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Swap Shift</p>
                <p class="text-sm text-slate-400">Request shift swap</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button @click="showCreateShiftModal = true" class="w-full flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-600/50 transition-colors">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clip-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-white font-medium">Create Shift</p>
                <p class="text-sm text-slate-400">Schedule new shift</p>
              </div>
            </div>
            <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Time Off Request Modal -->
    <div v-if="showTimeOffModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Request Time Off</h3>
          <button @click="showTimeOffModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="submitTimeOffRequest" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Type</label>
            <select v-model="timeOffForm.type" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="vacation">Vacation</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
            <input v-model="timeOffForm.startDate" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">End Date</label>
            <input v-model="timeOffForm.endDate" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Reason</label>
            <textarea v-model="timeOffForm.reason" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Please provide a reason..."></textarea>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showTimeOffModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Swap Shift Modal -->
    <div v-if="showSwapShiftModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Swap Shift</h3>
          <button @click="showSwapShiftModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="submitSwapShiftRequest" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Current Shift</label>
            <select v-model="swapShiftForm.currentShiftId" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="">Select current shift</option>
              <option value="1">Pipeline Maintenance - Jan 15</option>
              <option value="2">Safety Inspection - Jan 16</option>
              <option value="3">Equipment Repair - Jan 17</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">New Shift</label>
            <select v-model="swapShiftForm.newShiftId" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
              <option value="">Select new shift</option>
              <option value="4">Emergency Response - Jan 18</option>
              <option value="5">Pipeline Maintenance - Jan 19</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Reason</label>
            <textarea v-model="swapShiftForm.reason" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Why do you want to swap shifts?"></textarea>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showSwapShiftModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Create Shift Modal -->
    <div v-if="showCreateShiftModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-bold text-white">Create New Shift</h3>
          <button @click="showCreateShiftModal = false" class="text-slate-400 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <form @submit.prevent="submitCreateShift" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Job Title</label>
            <input v-model="createShiftForm.jobTitle" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., Pipeline Maintenance">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
            <input v-model="createShiftForm.date" type="date" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
          </div>
          
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
              <input v-model="createShiftForm.startTime" type="time" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">End Time</label>
              <input v-model="createShiftForm.endTime" type="time" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white">
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
            <input v-model="createShiftForm.location" type="text" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="e.g., ExxonMobil Site">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea v-model="createShiftForm.description" rows="3" class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white" placeholder="Brief description of the shift..."></textarea>
          </div>
          
          <div class="flex space-x-3">
            <button type="button" @click="showCreateShiftModal = false" class="flex-1 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg">
              Cancel
            </button>
            <button type="submit" class="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg">
              Create Shift
            </button>
          </div>
        </form>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AppLayout from '../components/layout/AppLayout.vue'
import { useUserStore } from '../stores/userStore'

const userStore = useUserStore()

// Types
interface Shift {
  id: number
  jobTitle: string
  date: string
  time: string
  location: string
  status: string
  description?: string
}

interface WeekDay {
  date: string
  name: string
  shifts: Shift[]
}

interface ScheduleStats {
  thisWeek: number
  hoursScheduled: number
  pendingRequests: number
  activeJobs: number
}

// Reactive data
const scheduleStats = ref<ScheduleStats>({
  thisWeek: 0,
  hoursScheduled: 0,
  pendingRequests: 0,
  activeJobs: 0
})

const isLoading = ref(true)
const currentWeek = ref(new Date())

// Modal states
const showTimeOffModal = ref(false)
const showSwapShiftModal = ref(false)
const showCreateShiftModal = ref(false)

// Form data
const timeOffForm = ref({
  startDate: '',
  endDate: '',
  reason: '',
  type: 'vacation'
})

const swapShiftForm = ref({
  currentShiftId: '',
  newShiftId: '',
  reason: ''
})

const createShiftForm = ref({
  jobTitle: '',
  date: '',
  startTime: '',
  endTime: '',
  location: '',
  description: ''
})

const weekDays = ref<WeekDay[]>([])
const upcomingShifts = ref<Shift[]>([])

// API Methods
const fetchScheduleData = async () => {
  try {
    isLoading.value = true
    
    // Fetch schedule statistics
    const statsResponse = await fetch('/api/scheduling/stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (statsResponse.ok) {
      const statsData = await statsResponse.json()
      scheduleStats.value = statsData
    }
    
    // Fetch weekly schedule
    const weekResponse = await fetch('/api/scheduling/weekly', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (weekResponse.ok) {
      const weekData = await weekResponse.json()
      weekDays.value = weekData
    }
    
    // Fetch upcoming shifts
    const upcomingResponse = await fetch('/api/scheduling/upcoming', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${userStore.authToken}`
      }
    })
    
    if (upcomingResponse.ok) {
      const upcomingData = await upcomingResponse.json()
      upcomingShifts.value = upcomingData
    }
    
  } catch (error) {
    console.error('Error fetching schedule data:', error)
    // Fallback to empty data
    scheduleStats.value = {
      thisWeek: 0,
      hoursScheduled: 0,
      pendingRequests: 0,
      activeJobs: 0
    }
    weekDays.value = []
    upcomingShifts.value = []
  } finally {
    isLoading.value = false
  }
}

// Methods
const previousWeek = async () => {
  currentWeek.value = new Date(currentWeek.value.getTime() - 7 * 24 * 60 * 60 * 1000)
  await fetchScheduleData()
}

const nextWeek = async () => {
  currentWeek.value = new Date(currentWeek.value.getTime() + 7 * 24 * 60 * 60 * 1000)
  await fetchScheduleData()
}

const getShiftBorderClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Confirmed': 'border-emerald-500',
    'Pending': 'border-yellow-500',
    'Cancelled': 'border-red-500'
  }
  return classes[status] || 'border-slate-500'
}

const getStatusClass = (status: string) => {
  const classes: { [key: string]: string } = {
    'Confirmed': 'bg-emerald-500/90',
    'Pending': 'bg-yellow-500/90',
    'Cancelled': 'bg-red-500/90'
  }
  return classes[status] || 'bg-slate-500/90'
}

// Form submission methods
const submitTimeOffRequest = () => {
  console.log('Time off request submitted:', timeOffForm.value)
  // Here you would typically make an API call
  alert('Time off request submitted successfully!')
  showTimeOffModal.value = false
  // Reset form
  timeOffForm.value = {
    startDate: '',
    endDate: '',
    reason: '',
    type: 'vacation'
  }
}

const submitSwapShiftRequest = () => {
  console.log('Swap shift request submitted:', swapShiftForm.value)
  // Here you would typically make an API call
  alert('Shift swap request submitted successfully!')
  showSwapShiftModal.value = false
  // Reset form
  swapShiftForm.value = {
    currentShiftId: '',
    newShiftId: '',
    reason: ''
  }
}

const submitCreateShift = () => {
  console.log('New shift created:', createShiftForm.value)
  // Here you would typically make an API call
  alert('New shift created successfully!')
  showCreateShiftModal.value = false
  // Reset form
  createShiftForm.value = {
    jobTitle: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    description: ''
  }
}

// Load data on mount
onMounted(() => {
  fetchScheduleData()
})
</script>

<style scoped>
.schedule-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 hover:border-emerald-500/50 transition-all duration-300;
}
</style>
